// SPDX-License-Identifier: Elastic-2.0
/**
 * discoveryMock — MSW handlers for A2.5 Network Discovery step (F-350).
 *
 * The discovery screen mounts and immediately fires POST /discovery/scan,
 * then polls GET /discovery/scan/:jobId until status === "complete", then
 * fetches GET /discovery/devices. Without these handlers any test that
 * navigates through A2.5 fails with onUnhandledRequest: "error".
 *
 * URL patterns use wildcards on the host so they match both:
 *   - http://localhost:8200/api/v1/discovery/* (vitest / unit tests)
 *   - /api/mirlai/v1/discovery/*              (add-on single-host mode)
 */
import { HttpResponse, http } from "msw";

const DISCOVERY_SCAN_RE = /\/discovery\/scan$/;
const DISCOVERY_SCAN_JOB_RE = /\/discovery\/scan\/[^/]+$/;
const DISCOVERY_DEVICES_RE = /\/discovery\/devices$/;

const FIXTURE_SCAN_RESPONSE = {
  job_id: "fixture-scan-01",
  status: "running",
};

const FIXTURE_SCAN_STATUS = {
  job_id: "fixture-scan-01",
  status: "complete",
  progress: 1.0,
  device_count: 2,
};

const FIXTURE_DISCOVERY_RESULT = {
  scan_id: "fixture-scan-01",
  started_at: "2026-06-01T12:00:00Z",
  completed_at: "2026-06-01T12:00:08Z",
  subnet: "192.168.0.0/24",
  status: "complete",
  hidden_randomized: 1,
  scan_duration_s: 8.0,
  devices: [
    {
      ip: "192.168.0.89",
      mac: "20:1f:3b:6c:a4:28",
      mac_randomized: false,
      hostname: "google-nest-mini.local",
      port: 8009,
      mdns_services: ["_googlecast._tcp.local."],
      instance_name: "Master bedroom",
      txt_records: { fn: "Master bedroom", md: "Google Nest Mini" },
      vendor: "Google",
      oui: "20:1F:3B",
      category: "Media / Voice",
      function: "Audio + cast target",
      model_family: "Google Nest Mini",
      room: "Master bedroom",
      room_confidence: "explicit",
      protocol_priority: [{ protocol: "cast_sdk", rationale: "Native", rank: 1 }],
      first_seen_at: null,
      last_seen_at: null,
      online: true,
    },
    {
      ip: "192.168.0.92",
      mac: "a8:bb:50:6b:93:ac",
      mac_randomized: false,
      hostname: null,
      port: null,
      mdns_services: [],
      instance_name: null,
      txt_records: {},
      vendor: "WiZ IoT Company Limited",
      oui: "A8:BB:50",
      category: "Lighting",
      function: "Smart bulb (WiZ) — cloud/UDP, no local API at MVP",
      model_family: "WiZ smart bulb",
      room: null,
      room_confidence: "none",
      protocol_priority: [],
      first_seen_at: null,
      last_seen_at: null,
      online: true,
    },
  ],
};

/**
 * MSW handler set for the F-350 discovery endpoints.
 * Add to `setupServer(...)` in any test that navigates through A2.5.
 */
export const discoveryHandlers = [
  http.post(DISCOVERY_SCAN_RE, () =>
    HttpResponse.json(FIXTURE_SCAN_RESPONSE, { status: 202 }),
  ),
  http.get(DISCOVERY_SCAN_JOB_RE, () =>
    HttpResponse.json(FIXTURE_SCAN_STATUS),
  ),
  http.get(DISCOVERY_DEVICES_RE, () =>
    HttpResponse.json(FIXTURE_DISCOVERY_RESULT),
  ),
  http.patch(DISCOVERY_DEVICES_RE, () =>
    new HttpResponse(null, { status: 204 }),
  ),
];
